// @flow

import _ from 'lodash';

import { Error } from 'error';

import UUID from 'uuid-js';

import Q from 'q';
import type { QPromise } from 'types/q';

import { noopLogger } from 'splunk-logger/js/noop-logger';
import type { SplunkLoggerInterface, SplunkRecord } from 'splunk-logger';

import { toNumber as businessOperationToNumber } from 'business-operation';
import type { BusinessOperation } from 'business-operation';
import type { ProductType } from 'product-type';

import type { AuthContextInterface } from 'auth-context';

import { sessionId } from 'session-id';

import {
	noopDataGetter,
	joinDataGetters,
} from './js/data-getters';

import { getStatisticsHeaders } from 'app-my-dms-insurance/app/utils/get-statistics-headers';

import type {
	StartDataGetter,
	ResolveDataGetter,
	RejectDataGetter,
} from './js/data-getters';

export * from './js/data-getters';

export type MinimalSplunkRecord = SplunkRecord | {
	// the rest will be filled from context
	message: {
		Message: 'Started' | 'Completed' | 'Failed',
	},
};

export type LogContextContentExt = {
	InsuranceProductType?: ProductType,
};

export type LogContextContent = {|
	splunkLogger?: SplunkLoggerInterface,

	businessOperation?: BusinessOperation,
	correlationId?: string,
	sessionId?: string,

	ext?: LogContextContentExt,
|};

export interface BusinessOperationContextInterface {
	replaceLogContext (LogContextInterface): void;
}

export type BusinessOperationFunction<T>
	= (LogContextInterface, BusinessOperationContextInterface) => Promise<T> | T;

export type BusinessOperationOptions = {
	dataGetters?: {
		start?: StartDataGetter | Array<StartDataGetter>,
		resolve?: ResolveDataGetter | Array<ResolveDataGetter>,
		reject?: RejectDataGetter | Array<RejectDataGetter>,
	},
};

type FollowupOptions = {
	CorrelationId: string,
	ext: Object,
};

export interface LogContextInterface {
	subcontext (LogContextContent): LogContextInterface,

	withAuthContext (AuthContextInterface): LogContextInterface,
	withLogContext (LogContextInterface): LogContextInterface,

	suboperation<T> (businessOperation: BusinessOperation,
				  operationFunction: ?BusinessOperationFunction<T>,
				  operationOptions?: BusinessOperationOptions): QPromise<T>,
	followup (FollowupOptions): void,

	push (MinimalSplunkRecord): void,
	flush (): QPromise<void>,

	+splunkLogger: SplunkLoggerInterface,

	+businessOperation: ?BusinessOperation,
	+correlationId: ?string,

	getAdditionalRequestHeaders (): Object,
}

function getStartData ({ dataGetters }: BusinessOperationOptions): Object {
	const getters = (dataGetters && dataGetters.start) || noopDataGetter;
	return ((joinDataGetters(getters): any): StartDataGetter)(); // XXX
}

function getResolveData (v: any, { dataGetters }: BusinessOperationOptions): Object {
	const getters = (dataGetters && dataGetters.resolve) || noopDataGetter;
	const startData = getStartData({ dataGetters });
	return _.extend(
		startData,
		((joinDataGetters(getters): any): ResolveDataGetter)(v) // XXX
	);
}

function getRejectData (e: Error, { dataGetters }: BusinessOperationOptions): Object {
	const getters = (dataGetters && dataGetters.reject) || noopDataGetter;
	const startData = getStartData({ dataGetters });
	return _.extend(
		startData,
		((joinDataGetters(getters): any): RejectDataGetter)(e) // XXX
	);
}

class LogContext implements LogContextInterface {
	_content: LogContextContent

	constructor (content: LogContextContent) {
		this._content = content;
	}

	subcontext (extraContent: LogContextContent) {
		return new LogContext(_.defaultsDeep({}, extraContent, this._content));
	}

	withAuthContext (authContext: AuthContextInterface): LogContextInterface {
		return this.subcontext({
			splunkLogger: this.splunkLogger.withAuthContext(authContext),
		});
	}

	withLogContext (logContext: LogContextInterface): LogContextInterface {
		return this.subcontext({
			splunkLogger: this.splunkLogger.withLogContext(logContext),
		});
	}

	start = (operationOptions) => {
		this.push({
			message: {
				Message: 'Started',
				ext: getStartData(operationOptions),
			},
		});
	}

	fail = (operationOptions, err) => {
		this.push({
			message: {
				Message: 'Failed',
				ext: getRejectData(err, operationOptions),
			},
		});
	}

	complete = (operationOptions, value) => {
		this.push({
			message: {
				Message: 'Completed',
				ext: getResolveData(value, operationOptions),
			},
		});
	}
	definedSubcontext (businessOperation: BusinessOperation) {
		const correlationId = this.correlationId || UUID.create().hex;

		const subcontext = this.subcontext({
			businessOperation,
			correlationId,
		});

		return subcontext;
	}


	suboperation<T> (businessOperation: BusinessOperation,
		operationFunction_: ?BusinessOperationFunction<T>,
		operationOptions?: BusinessOperationOptions = {}): QPromise<T> {

		const operationFunction = operationFunction_;

		return (Q(): QPromise<T>)
		.then(() => {

			let subcontext = this.definedSubcontext(
				businessOperation
			);
		
			subcontext.start(operationOptions);
		
			const go = (): QPromise<mixed> => {
				if (operationFunction) {
					return Q()
						.tap(() => this.flush())
						.then(() => {
							return operationFunction(subcontext, {
								replaceLogContext (newLogContext: LogContextInterface) {
									subcontext = newLogContext;
								},
							});
						});
				}
				return Q();
			};

			return go()
				.catch(err => {
					subcontext.fail(operationOptions, err);
					throw err;
				})
				.tap(value => {
					subcontext.complete(operationOptions, value);
				});
		});
	}


	followup (options: FollowupOptions): void {
		const subcontext = this.subcontext({
			correlationId: options.CorrelationId,
		});

		const push = (message, ext = {}) => {
			subcontext.push({
				message: {
					Message: message,
					Action: 'MessageForClient',
					ext,
				},
			});
		};

		push('Started', options.ext);
		push('Completed', options.ext);
	}

	push (record: MinimalSplunkRecord) {
		record = _.defaultsDeep({}, record, {
			message: {
				BusinessOperationCode: this.businessOperation
					&& businessOperationToNumber(this.businessOperation),
				CorrelationId: this.correlationId,
				ext: _.defaultsDeep({}, this._content.ext || {}, {
					SessionId: this.sessionId,
				}),
			},
		});

		record = (record: SplunkRecord);

		record.message.ext = JSON.parse(JSON.stringify(record.message.ext, (k, v) => {
			return _.isFunction(v) ? v() : v;
		}));

		this.splunkLogger.push(record);
	}

	flush () {
		return this.splunkLogger.flush();
	}

	get businessOperation () {
		return this._content.businessOperation;
	}

	get correlationId () {
		return this._content.correlationId;
	}

	get sessionId () {
		return this._content.sessionId || sessionId();
	}

	get splunkLogger () {
		return this._content.splunkLogger || noopLogger;
	}

	getAdditionalRequestHeaders () {
		const statHeaders = getStatisticsHeaders();
		return _.pickBy({
			'x-splunk-log-correlationid': this.correlationId,
			'x-splunk-log-sessionid': this.sessionId,
			...statHeaders,
		}, Boolean);
	}
}

export const root: LogContextInterface = new LogContext({
	ext: {
		_debug: {
			ui: {
				version: '__VERSION__', // replaced with actual build version by browserify
			},
		},
	},
});
